import { matchRoutes, useLocation, useParams, useResolvedPath } from 'react-router-dom';

import { TRouteItems, TRoutesData, TRoutesTree } from 'types/routes';

// ───────────────────────────────────────────────────────────────────────────────
// Типы
// ───────────────────────────────────────────────────────────────────────────────
type TransformedRoute = {
    path: string;
    element: JSX.Element;
    children?: TransformedRoute[];
    __originalRoute: TRoutesTree[0];
};

// ───────────────────────────────────────────────────────────────────────────────
// Вспомогательные функции
// ───────────────────────────────────────────────────────────────────────────────
const getParamNames = (path: string | string[] | undefined) => {
    if (!path) return [];
    const pathStr = Array.isArray(path) ? path[0] : path;
    return (pathStr.match(/:[^/]+/g) || []).map((p) => p.substring(1));
};

const processText = (
    value: string | ((params: Record<string, string>) => string) | undefined,
    params: Record<string, string>,
    paramNames: string[] = []
) => {
    if (value === undefined) return undefined;
    if (typeof value === 'function') {
        if (paramNames.some((p) => params[p] === undefined)) return undefined;
        try {
            return value(params);
        } catch {
            return undefined;
        }
    }
    return value;
};

// ───────────────────────────────────────────────────────────────────────────────
// Сборщик виртуальных маршрутов
// ───────────────────────────────────────────────────────────────────────────────
const collectVirtualElements = (
    routes: TRoutesTree,
    currentPath: string,
    currentParams: Record<string, string>,
    basePath = '',
    ancestorPath = ''
): TRouteItems => {
    const virtuals: TRouteItems = [];
    const segments = currentPath.split('/').filter(Boolean);

    for (const route of routes) {
        if (!route.element) {
            // Виртуальный узел
            if (!route.path) {
                // Безусловный
                const title = processText(route.title, {}, []);
                const breadcrumb = processText(route.breadcrumb, {}, []);
                virtuals.push({
                    path: ancestorPath || '/',
                    title: title || breadcrumb,
                    breadcrumb: breadcrumb || title,
                    icon: route.icon || <></>,
                    custom: route.custom,
                });
            } else {
                // Условный
                const virtualPath = Array.isArray(route.path) ? route.path[0] : route.path;
                let matchInfo: { params: Record<string, string>; idx: number } | null = null;

                if (virtualPath.startsWith(':')) {
                    const name = virtualPath.slice(1);
                    const value = currentParams[name];
                    if (value) {
                        const idx = segments.lastIndexOf(value); // <-- главное исправление
                        if (idx !== -1) matchInfo = { params: { [name]: value }, idx };
                    }
                } else {
                    const idx = segments.lastIndexOf(virtualPath); // тоже берём последнее вхождение
                    if (idx !== -1) matchInfo = { params: {}, idx };
                }

                if (matchInfo) {
                    const params = { ...currentParams, ...matchInfo.params };
                    const paramNames = getParamNames(route.path);
                    const title = processText(route.title, params, paramNames);
                    const breadcrumb = processText(route.breadcrumb, params, paramNames);

                    const navPath = '/' + segments.slice(0, matchInfo.idx + 1).join('/');
                    virtuals.push({
                        path: navPath,
                        title: title || breadcrumb,
                        breadcrumb: breadcrumb || title,
                        icon: route.icon || <></>,
                        custom: route.custom,
                    });

                    if (route.children) {
                        virtuals.push(
                            ...collectVirtualElements(
                                route.children,
                                currentPath,
                                params,
                                '',
                                navPath
                            )
                        );
                    }
                }
            }
        } else if (route.children) {
            virtuals.push(
                ...collectVirtualElements(
                    route.children,
                    currentPath,
                    currentParams,
                    basePath,
                    ancestorPath
                )
            );
        }
    }
    return virtuals;
};

// ───────────────────────────────────────────────────────────────────────────────
// Трансформатор маршрутов
// ───────────────────────────────────────────────────────────────────────────────
const transformRoutesForMatching = (routes: TRoutesTree): TransformedRoute[] =>
    routes
        .map((r) => {
            if (!r.element || !r.path)
                return r.children ? transformRoutesForMatching(r.children) : [];
            const paths = Array.isArray(r.path) ? r.path : [r.path];
            return paths.map((p) => ({
                path: p,
                element: r.element as JSX.Element,
                children: r.children ? transformRoutesForMatching(r.children) : undefined,
                __originalRoute: r,
            }));
        })
        .flat(2) as TransformedRoute[];

// ───────────────────────────────────────────────────────────────────────────────
// Хук
// ───────────────────────────────────────────────────────────────────────────────
export const useRoutesData = (routes: TRoutesTree): TRoutesData => {
    const { pathname } = useLocation();
    const params = useParams();

    const parentPath = useResolvedPath('..');
    const currentPath = useResolvedPath('.');
    const base = (parentPath.pathname !== '/' ? parentPath : currentPath).pathname.replace(
        /\/$/,
        ''
    );

    const rel = pathname.startsWith(base) ? pathname.slice(base.length) || '/' : pathname;
    const normalized = rel.startsWith('/') ? rel : `/${rel}`;

    // Виртуальные элементы
    const virtualRoutes = collectVirtualElements(routes, pathname, params, '', base);

    // Реальные элементы
    const matches = matchRoutes(transformRoutesForMatching(routes), normalized) || [];
    const realRoutes = matches.map((m) => {
        const orig = m.route.__originalRoute;
        const names = getParamNames(orig.path);
        const title = processText(orig.title, m.params || {}, names);
        const breadcrumb = processText(orig.breadcrumb, m.params || {}, names);
        return {
            path: base + (m.pathname === '/' ? '' : m.pathname),
            title: title || breadcrumb,
            breadcrumb: breadcrumb || title,
            icon: orig.icon || <></>,
            custom: orig.custom,
            onClick: orig.onClick,
            cardWrapper: orig.cardWrapper,
        };
    });

    // Дочерние элементы
    let children: TRouteItems = [];
    const last = matches.at(-1);
    if (last?.route.__originalRoute?.children) {
        const parentLink = base + (last.pathname === '/' ? '' : last.pathname);
        children = last.route.__originalRoute.children
            .filter((c) => c.element)
            .map((c) => {
                const names = getParamNames(c.path);
                const raw = Array.isArray(c.path) ? c.path[0] : c.path || '';
                const childPath = raw.startsWith('/')
                    ? raw
                    : `${parentLink}/${raw}`.replace(/\/+/g, '/');
                const title = processText(c.title, params, names);
                const breadcrumb = processText(c.breadcrumb, params, names);
                const fallback = Array.isArray(c.path) ? c.path[0] : c.path;
                return {
                    path: childPath,
                    title: title || breadcrumb || fallback,
                    breadcrumb: breadcrumb || title || fallback,
                    icon: c.icon || <></>,
                    custom: c.custom,
                    onClick: c.onClick,
                    cardWrapper: c.cardWrapper,
                };
            });
    }

    return { currentRoutes: [...virtualRoutes, ...realRoutes], childrenRoutes: children };
};
